import React, { ReactElement, useContext, useState } from 'react'
import { SelfProvisioning as SelfProvisioningLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import Modal from '../../Common/Components/Modal'
import { StandardContexts } from '../../Common/Components/StandardContexts'
import { CommonFormContext, FormContextType } from '../../Common/Contexts/CommonFormContext'
import { ModalContextProvider } from '../../Common/Contexts/ModalContext'
import { ReactState } from '../../Common/Types/Types'
import { SelfProvisioningConfigContextProvider } from '../Contexts/SelfProvisioningConfigContext'
import { SelfProvisioningContextProvider } from '../Contexts/SelfProvisioningContext'
import { SelfProvisioning } from '../Main/SelfProvisioning'
import { $window } from '../../../NonReact/common/AkixiWindow'
import { deleteTelSysAndPartitions } from '../../../NonReact/common/API/EntityAPIClient'

/**
 * Self Provisioning modal create the modal wrapper for the Self Provisioning wizard.
 *
 * @returns - The modal wrapper for the Self Provisioning wizard.
 */
export const SelfProvisioningModal = (): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(SelfProvisioningLanguage.NAME)

    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    // Whether the modal window is open.
    const [isModalOpen, setModalOpen]: ReactState<boolean> = useState<boolean>(true)

    // Telephony server to delete if the modal is closed.
    const [deleteableTelSys, setDeleteableTelSys]: ReactState<Nullable<number>> = useState<Nullable<number>>()
    // Partition to delete if the modal is closed.
    const [deleteablePartition, setDeleteablePartition]: ReactState<Nullable<number>> = useState<Nullable<number>>()

    const [modalTitle, setModalTitle]: ReactState<string> = useState<string>(
        language.get(SelfProvisioningLanguage.SelfProvisioning_ModalTitle)
    )

    const closeModal = (): void => {
        $window.disableHotKeys = false
        // Component is controlled externally.
        setModalOpen(false)
    }

    return (
        <ModalContextProvider
            modalInput={{
                closeModal,
                setModalTitle
            }}
        >
            <StandardContexts>
                <SelfProvisioningConfigContextProvider>
                    <SelfProvisioningContextProvider
                        deleteableTelSysState={[deleteableTelSys, setDeleteableTelSys]}
                        deleteablePartitionState={[deleteablePartition, setDeleteablePartition]}
                    >
                        <Modal
                            isOpen={isModalOpen}
                            closeOnBackdropClick={false}
                            isClosableWithXButton={true}
                            onOpen={(): void => {
                                // Add contextual doc topic ID to the window object which is used
                                // in old JSP logic that handles F1 key press.
                                // setModalHelpTopicID(DocTopic.SELF_PROVISIONING_WIZARD)
                            }}
                            onClose={closeModal}
                            onExited={(): void => {
                                deleteTelSysAndPartitions(deleteableTelSys, deleteablePartition)

                                // Remove the contextual doc topic ID from the window that is used
                                // in old JSP logic that handles F1 key press.
                                // When removed the previous logic will work and the modal window doc topic will be not considered.
                                // clearModalHelpTopic()
                            }}
                            inlineExitPromptSettings={
                                isVarNotEmpty(deleteableTelSys) ||
                                isVarNotEmpty(deleteablePartition) ||
                                commonForm?.isTouched
                                    ? {
                                          confirmButtonText: language.get(
                                              SelfProvisioningLanguage.SelfProvisioning_ExitPrompt_Confirm
                                          ),
                                          cancelButtonText: language.get(
                                              SelfProvisioningLanguage.SelfProvisioning_ExitPrompt_Cancel
                                          ),
                                          message: language.get(
                                              SelfProvisioningLanguage.SelfProvisioning_ExitPrompt_Message
                                          ),
                                          onConfirm: closeModal
                                      }
                                    : undefined
                            }
                        >
                            <div className='modal-title-report-config'>{modalTitle}</div>
                            <div className='self-provisioning'>
                                <SelfProvisioning />
                            </div>
                        </Modal>
                    </SelfProvisioningContextProvider>
                </SelfProvisioningConfigContextProvider>
            </StandardContexts>
        </ModalContextProvider>
    )
}
